import React, { useContext, useState } from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { HeadCell } from '../../../common/table/EnhancedTableHead';
import { EnhancedTable } from '../../../common/table/EnhancedTable';
import { FeatureDefinitionView } from '../../../generated/ApiClient';
import FeatureDialog from './FeatureDialog';
import { ClassificationContext } from '../context/ClassificationContext.types';

interface FeatureDefinitionRow {
  id: string;
  name: string;
  featureType: string;
  format: string;
}

export const FeaturesList = () => {
  const { t } = useTranslation();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState<FeatureDefinitionView | null>(null);
  const { features, refetchFeatures, isFeaturesLoading } = useContext(ClassificationContext);

  const handleAddFeature = () => {
    setEditingFeature(null);
    setIsDialogOpen(true);
  };

  const handleEditFeature = (feature: FeatureDefinitionView) => {
    setEditingFeature(feature);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    refetchFeatures();
  };

  const headCells: readonly HeadCell<FeatureDefinitionRow>[] = [
    {
      id: 'name',
      disablePadding: false,
      label: t('classification.features.name'),
      allowSorting: true,
      allowFiltering: true,
    },
    {
      id: 'featureType',
      disablePadding: false,
      label: t('classification.features.type'),
      allowSorting: true,
      allowFiltering: true,
    },
    {
      id: 'format',
      disablePadding: false,
      label: t('classification.features.format'),
      allowSorting: true,
      allowFiltering: true,
    },
    {
      id: 'id',
      disablePadding: false,
      label: t('classification.features.id'),
      allowSorting: true,
      allowFiltering: true,
    },
  ];

  const handleRowClick = (e: React.MouseEvent<unknown>, id: string) => {
    e.stopPropagation();

    const feature = features.find((x) => x.id === id);
    if (feature) handleEditFeature(feature);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h1">{t('classification.features.title')}</Typography>
        <Button variant="contained" onClick={handleAddFeature}>
          {t('classification.features.addFeature')}
        </Button>
      </Box>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <EnhancedTable
          rows={features}
          total={features?.length}
          loading={isFeaturesLoading}
          headCells={headCells}
          hideCheckbox={true}
          variant="default"
          onRowClick={handleRowClick}
        />
      </Paper>

      <FeatureDialog
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        feature={editingFeature}
        onClose={handleDialogClose}
      />
    </Box>
  );
};

export default FeaturesList;
